import React, { useEffect, useId, useState } from 'react';
import Dialog from '../../common/Dialog';
import { ClaimsService } from '../../../services/billing/claims.service';
import {
  ALLOWED_NEXT_STATUSES, CLAIM_STATUS_BADGE, SUBMISSION_METHOD_LABELS
} from '../../../dtos/billing/claims.dto';
import type {
  Claim, ClaimStatus, SecondaryBilled, SubmissionMethod, UpdateClaimStatusRequest
} from '../../../dtos/billing/claims.dto';

interface ClaimStatusDialogProps {
  claim: Claim | null;
  onClose: () => void;
  onSaved: (claim: Claim) => void;
}

const todayLocal = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const inputClass = 'w-full rounded-md border border-slate-300 px-3 py-2 text-base focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500';

/**
 * Manual register update (REQ-BIL-10/13): Submitted (date, method), Accepted (payer), Paid (date,
 * amount), Denied / Rejected (reason), and the "Secondary billed" flag with a note.
 */
const ClaimStatusDialog: React.FC<ClaimStatusDialogProps> = ({ claim, onClose, onSaved }) => {
  const id = useId();
  const nextStatuses = claim ? ALLOWED_NEXT_STATUSES[claim.status] || [] : [];
  const [status, setStatus] = useState<ClaimStatus | ''>('');
  const [date, setDate] = useState(todayLocal());
  const [method, setMethod] = useState<SubmissionMethod>('PORTAL');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  // Phase 17 (REQ-CLM-09): payer adjustment reason code and remark codes (codes only)
  const [reasonCode, setReasonCode] = useState('');
  const [remarkCodes, setRemarkCodes] = useState('');
  const [payerClaimNumber, setPayerClaimNumber] = useState('');
  const [secondaryBilled, setSecondaryBilled] = useState<SecondaryBilled | ''>('');
  const [secondaryNote, setSecondaryNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!claim) return;
    setStatus(nextStatuses[0] || '');
    setDate(todayLocal());
    setMethod('PORTAL');
    setAmount(claim.totalAmount != null ? String(claim.totalAmount) : '');
    setReason('');
    setPayerClaimNumber(claim.payerClaimNumber || '');
    setSecondaryBilled(claim.secondaryBilled || '');
    setSecondaryNote(claim.secondaryNote || '');
    setError(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- reset only when a different claim opens
  }, [claim?.claimId]);

  if (!claim) return null;

  const needsReason = status === 'DENIED' || status === 'REJECTED';
  const valid = (!status || (status === 'SUBMITTED' ? Boolean(date && method) : true))
    && (status !== 'PAID' || (Boolean(date) && amount !== '' && Number(amount) >= 0))
    && (!needsReason || reason.trim().length >= 3)
    && (Boolean(status) || secondaryBilled !== '' || payerClaimNumber.trim() !== (claim.payerClaimNumber || ''));

  const save = async () => {
    const payload: UpdateClaimStatusRequest = {};
    if (status) payload.status = status;
    if (status === 'SUBMITTED') { payload.submissionDate = date; payload.submissionMethod = method; }
    if (status === 'ACCEPTED' && date) payload.acceptedDate = date;
    if (status === 'PAID') { payload.paymentDate = date; payload.paymentAmount = Number(amount); }
    if (needsReason) {
      payload.reason = reason.trim();
      if (date) payload.date = date;
      if (reasonCode.trim()) payload.denialReasonCode = reasonCode.trim().toUpperCase();
      const remarks = remarkCodes.toUpperCase().split(/[\s,]+/).filter(Boolean);
      if (remarks.length) payload.remarkCodes = remarks;
    }
    if (payerClaimNumber.trim() && payerClaimNumber.trim() !== (claim.payerClaimNumber || '')) payload.payerClaimNumber = payerClaimNumber.trim();
    if (secondaryBilled) { payload.secondaryBilled = secondaryBilled; payload.secondaryNote = secondaryNote.trim(); }
    setSaving(true);
    setError(null);
    try {
      onSaved(await ClaimsService.updateClaimStatus(claim.claimId, payload));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update the claim');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} labelledBy={`${id}-title`}>
      <h3 id={`${id}-title`} className="text-lg font-semibold text-slate-800">Update claim</h3>
      <p className="mt-1 text-sm text-slate-500">
        {claim.patientName} · {ClaimsService.formatDate(claim.dosFrom)}–{ClaimsService.formatDate(claim.dosTo)} · {ClaimsService.formatCurrency(claim.totalAmount)}
      </p>

      <div className="mt-4 space-y-3 text-left">
        <div>
          <label htmlFor={`${id}-status`} className="mb-1 block text-sm font-medium text-slate-700">New status</label>
          <select id={`${id}-status`} value={status} onChange={(e) => setStatus(e.target.value as ClaimStatus | '')} className={inputClass}>
            <option value="">No status change</option>
            {nextStatuses.map((s) => <option key={s} value={s}>{CLAIM_STATUS_BADGE[s].label}</option>)}
          </select>
        </div>

        {status && (
          <div>
            <label htmlFor={`${id}-date`} className="mb-1 block text-sm font-medium text-slate-700">
              {status === 'SUBMITTED' ? 'Submission date' : status === 'PAID' ? 'Payment date' : 'Date'}
            </label>
            <input id={`${id}-date`} type="date" value={date} onChange={(e) => setDate(e.target.value)} className={inputClass} />
          </div>
        )}

        {status === 'SUBMITTED' && (
          <div>
            <label htmlFor={`${id}-method`} className="mb-1 block text-sm font-medium text-slate-700">Submission method</label>
            <select id={`${id}-method`} value={method} onChange={(e) => setMethod(e.target.value as SubmissionMethod)} className={inputClass}>
              {(Object.keys(SUBMISSION_METHOD_LABELS) as SubmissionMethod[]).map((m) => <option key={m} value={m}>{SUBMISSION_METHOD_LABELS[m]}</option>)}
            </select>
          </div>
        )}

        {status === 'PAID' && (
          <div>
            <label htmlFor={`${id}-amount`} className="mb-1 block text-sm font-medium text-slate-700">Amount paid</label>
            <input id={`${id}-amount`} type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className={inputClass} />
          </div>
        )}

        {needsReason && (
          <div>
            <label htmlFor={`${id}-reason`} className="mb-1 block text-sm font-medium text-slate-700">{status === 'DENIED' ? 'Denial reason' : 'Rejection reason'}</label>
            <textarea id={`${id}-reason`} rows={2} maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} className={inputClass} />
            <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
              <div>
                <label htmlFor={`${id}-reason-code`} className="mb-1 block text-sm font-medium text-slate-700">Reason code (from the remittance)</label>
                <input id={`${id}-reason-code`} maxLength={10} value={reasonCode} placeholder="e.g. CO-197" onChange={(e) => setReasonCode(e.target.value)} className={inputClass} />
              </div>
              <div>
                <label htmlFor={`${id}-remark-codes`} className="mb-1 block text-sm font-medium text-slate-700">Remark codes</label>
                <input id={`${id}-remark-codes`} value={remarkCodes} placeholder="e.g. N54, M62" onChange={(e) => setRemarkCodes(e.target.value)} className={inputClass} />
              </div>
            </div>
            <p className="mt-1 text-xs text-slate-500">Pelora shows the correct-or-appeal deadline from the last date of service on this claim.</p>
          </div>
        )}

        <div>
          <label htmlFor={`${id}-icn`} className="mb-1 block text-sm font-medium text-slate-700">Payer claim number (ICN)</label>
          <input id={`${id}-icn`} type="text" maxLength={50} value={payerClaimNumber} onChange={(e) => setPayerClaimNumber(e.target.value)} className={inputClass} />
          <p className="mt-1 text-xs text-slate-500">Needed later for a corrected claim (box 22).</p>
        </div>

        <fieldset className="rounded-md border border-slate-200 p-3">
          <legend className="px-1 text-sm font-medium text-slate-700">Secondary billed</legend>
          <div className="flex gap-4 text-sm">
            {(['Y', 'N', 'NA'] as SecondaryBilled[]).map((v) => (
              <label key={v} className="flex items-center gap-1">
                <input type="radio" name={`${id}-secondary`} checked={secondaryBilled === v} onChange={() => setSecondaryBilled(v)} />
                {v === 'Y' ? 'Yes' : v === 'N' ? 'No' : 'No secondary'}
              </label>
            ))}
          </div>
          <label htmlFor={`${id}-secnote`} className="mt-2 block text-xs text-slate-600">Note</label>
          <input id={`${id}-secnote`} type="text" maxLength={500} value={secondaryNote} onChange={(e) => setSecondaryNote(e.target.value)} className={inputClass} disabled={!secondaryBilled} />
        </fieldset>

        {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
      </div>

      <div className="mt-5 flex justify-end gap-2">
        <button type="button" onClick={onClose} className="rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-700" disabled={saving}>Cancel</button>
        <button type="button" onClick={save} disabled={!valid || saving} className="rounded-md bg-slate-800 px-3 py-2 text-sm text-white disabled:bg-slate-400">
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </Dialog>
  );
};

export default ClaimStatusDialog;
